import React from "react";
import Logo from "../../assets/images/jamb_logo.svg";
import CustomSelect from "../forms/custom-select.tsx";
import { EligibilityFormProps } from "./model.ts";

const loadingSx = {
  height: "38px",
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "loading 1.5s infinite",
  borderRadius: "4px",
  marginTop: "8px",
};

const EligibilityForm: React.FC<EligibilityFormProps> = ({
  formData,
  institutionTypes,
  institutionCategories,
  institutions,
  programs,
  loadingPrograms,
  olevelSubjects,
  alevelSubjects,
  utmeSubjects,
  handleChange,
  handleSubmit,
  handleInstitutionTypeChange,
  handleInstitutionCategoryChange,
  handleSubjectSelection,
  loading,
}) => {
  const options = (items) =>
    items.map((item) => ({
      label: item.title || item.name || item,
      value: item.itemid || item.id || item,
    }));

  const filteredOlevelCredits = olevelSubjects.filter(
    (subject) =>
      !formData.olevelPasses.some((pass) => pass.value === subject.value)
  );
  const filteredOlevelPasses = olevelSubjects.filter(
    (subject) =>
      !formData.olevelCredits.some((credit) => credit.value === subject.value)
  );

  const filteredAlevelCredits = alevelSubjects.filter(
    (subject) =>
      !formData.alevelPasses.some((pass) => pass.value === subject.value)
  );
  const filteredAlevelPasses = alevelSubjects.filter(
    (subject) =>
      !formData.alevelCredits.some((credit) => credit.value === subject.value)
  );

  {
    loadingPrograms && <div style={loadingSx}></div>;
  }
  return (
    <form onSubmit={handleSubmit} className="w-full px-4 md:px-10">
      <div className="w-full max-w-7xl mx-auto">
        <div className="py-8">
          <div className="flex items-center space-x-1 mb-10">
            <img className="w-24" src={Logo} alt="jamb_logo" />
            <div>
              <h1 className="text-xl font-bold text-[#505458] mb-1">
                JAMB Eligibility Checker
              </h1>
              <p className="text-gray-600 text-sm uppercase">
                Verify your admission requirements instantly
              </p>
            </div>
          </div>

          <div className="mb-8 bg-white p-8 rounded-2xl border-2 border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-1.5 h-8 bg-gradient-to-b from-green-600 to-green-400 rounded-full"></div>
              <h4 className="font-bold text-[#505458] text-xl">
                Entry & Program Details
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold uppercase text-[#797979]">
                  Mode of Entry
                </label>
                <CustomSelect
                  placeholder="Select mode of entry"
                  options={options(["UTME", "Direct Entry"])}
                  onChange={(v) => handleChange("entryMode", v)}
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold uppercase text-[#797979]">
                  Institution Type
                </label>
                <CustomSelect
                  placeholder="Select institution type"
                  options={institutionTypes}
                  value={
                    institutionTypes.find(
                      (type) => type.value === formData.institutionTypeId
                    ) || null
                  }
                  onChange={handleInstitutionTypeChange}
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold uppercase text-[#797979]">
                  Institution Category
                </label>
                <CustomSelect
                  placeholder="Select category"
                  options={institutionCategories}
                  value={
                    institutionCategories.find(
                      (cat) => cat.value === formData.institutionCategoryId
                    ) || null
                  }
                  onChange={handleInstitutionCategoryChange}
                  isDisabled={!formData.institutionType}
                  isClearable
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold uppercase text-[#797979]">
                  Institution
                </label>
                <CustomSelect
                  placeholder="Select Institution"
                  options={institutions}
                  value={
                    institutions.find(
                      (inst) => inst.value === formData.institutionId
                    ) || null
                  }
                  onChange={(v) => handleChange("institution", v)}
                  isDisabled={!formData.institutionCategory}
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold uppercase text-[#797979]">
                  Program
                </label>
                <CustomSelect
                  placeholder={
                    loadingPrograms ? "Loading programs..." : "Select Program"
                  }
                  options={programs || []}
                  onChange={(v) => handleChange("program", v)}
                  value={
                    (programs || []).find(
                      (p) => p.label === formData.program
                    ) || null
                  }
                  isDisabled={!formData.institutionId || loadingPrograms}
                  isLoading={loadingPrograms}
                  isClearable
                />

                {programs.length === 0 &&
                  !loadingPrograms &&
                  formData.institutionId && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() =>
                          handleChange("institution", {
                            label: formData.institution,
                            value: formData.institutionId,
                          })
                        }
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Retry loading programs
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="mb-8 bg-white p-8 rounded-2xl border-2 border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-1.5 h-8 bg-gradient-to-b from-blue-600 to-blue-400 rounded-full"></div>
              <h4 className="font-bold text-[#505458] text-xl">
                O'Level Selection
              </h4>
            </div>
            <p className="text-gray-600 text-sm mb-6 ml-4">
              Specify your subjects for O'Level Credits and Passes.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-[#797979] uppercase tracking-wide">
                  O'Level Credits (A1-C6)
                </label>
                <CustomSelect
                  isMulti
                  placeholder="Select subjects with credits..."
                  options={filteredOlevelCredits}
                  value={formData.olevelCredits}
                  onChange={(selected) => {
                    const total =
                      (selected?.length || 0) + formData.olevelPasses.length;
                    const limited =
                      total > 9
                        ? selected?.slice(0, 9 - formData.olevelPasses.length)
                        : selected || [];
                    handleSubjectSelection("olevelCredits", limited);
                  }}
                  closeMenuOnSelect={false}
                  isOptionDisabled={() =>
                    formData.olevelCredits.length +
                      formData.olevelPasses.length >=
                    9
                  }
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-[#797979] uppercase tracking-wide">
                  O'Level Passes (D7-E8)
                </label>
                <CustomSelect
                  isMulti
                  placeholder="Select subjects with passes..."
                  options={filteredOlevelPasses}
                  value={formData.olevelPasses}
                  onChange={(selected) => {
                    const total =
                      (selected?.length || 0) + formData.olevelCredits.length;
                    const limited =
                      total > 9
                        ? selected?.slice(0, 9 - formData.olevelCredits.length)
                        : selected || [];
                    handleSubjectSelection("olevelPasses", limited);
                  }}
                  closeMenuOnSelect={false}
                  isOptionDisabled={() =>
                    formData.olevelCredits.length +
                      formData.olevelPasses.length >=
                    9
                  }
                />
              </div>
            </div>

            <div className="mt-3 text-right">
              <span className="inline-block bg-blue-50 text-blue-700 text-sm font-semibold px-3 py-1 rounded-lg">
                Total O'Level Subjects Selected:{" "}
                {formData.olevelCredits.length + formData.olevelPasses.length}/9
              </span>
            </div>
          </div>

          <div className="mb-8 bg-white p-8 rounded-2xl border-2 border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-1.5 h-8 bg-gradient-to-b from-blue-600 to-blue-400 rounded-full"></div>
              <h4 className="font-bold text-[#505458] text-xl">
                A'Level Selection
              </h4>
            </div>
            <p className="text-gray-600 text-sm mb-6 ml-4">
              Specify your subjects for A'Level Credits and Passes.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-[#797979] uppercase tracking-wide">
                  A'Level Credits (A1-C6)
                </label>
                <CustomSelect
                  isMulti
                  placeholder="Select A'Level subjects..."
                  options={filteredAlevelCredits}
                  value={formData.alevelCredits}
                  onChange={(selected) => {
                    const total =
                      (selected?.length || 0) + formData.alevelPasses.length;
                    const limited =
                      total > 9
                        ? selected?.slice(0, 9 - formData.alevelPasses.length)
                        : selected || [];
                    handleSubjectSelection("alevelCredits", limited);
                  }}
                  closeMenuOnSelect={false}
                  isOptionDisabled={() =>
                    formData.alevelCredits.length +
                      formData.alevelPasses.length >=
                    9
                  }
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-[#797979] uppercase tracking-wide">
                  A'Level Passes (D7-E8)
                </label>
                <CustomSelect
                  isMulti
                  placeholder="Select A'Level subjects..."
                  options={filteredAlevelPasses}
                  value={formData.alevelPasses}
                  onChange={(selected) => {
                    const total =
                      (selected?.length || 0) + formData.alevelCredits.length;
                    const limited =
                      total > 9
                        ? selected?.slice(0, 9 - formData.alevelCredits.length)
                        : selected || [];
                    handleSubjectSelection("alevelPasses", limited);
                  }}
                  closeMenuOnSelect={false}
                  isOptionDisabled={() =>
                    formData.alevelCredits.length +
                      formData.alevelPasses.length >=
                    9
                  }
                />
              </div>
            </div>

            <div className="mt-3 text-right">
              <span className="inline-block bg-blue-50 text-blue-700 text-sm font-semibold px-3 py-1 rounded-lg">
                Total A'Level Subjects Selected:{" "}
                {formData.alevelCredits.length + formData.alevelPasses.length}/9
              </span>
            </div>
          </div>

          <div className="mb-10 bg-white p-8 rounded-2xl border-2 border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-1.5 h-8 bg-gradient-to-b from-amber-600 to-amber-400 rounded-full"></div>
              <h4 className="font-bold text-[#505458] text-xl">
                UTME Subjects Combination
              </h4>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-[#797979] uppercase tracking-wide">
                Select 4 UTME Subjects (Including English)
              </label>
              <CustomSelect
                isMulti
                placeholder="Select 4 UTME subjects..."
                options={utmeSubjects}
                value={formData.utmeSubjects}
                onChange={(selected) => {
                  const limitedSelection =
                    selected && selected.length > 4
                      ? selected.slice(0, 4)
                      : selected;
                  handleSubjectSelection("utmeSubjects", limitedSelection);
                }}
                closeMenuOnSelect={false}
                isOptionDisabled={() => formData.utmeSubjects.length >= 4}
              />
              <p className="text-sm font-medium text-[#797979]">
                {formData.utmeSubjects.length}/4 subjects selected
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12">
            <button
              type="submit"
              disabled={formData.utmeSubjects.length !== 4 || !formData.program}
              className={`w-full sm:w-auto group relative px-10 py-4 rounded-xl font-bold text-base text-white overflow-hidden transition-all duration-300 ${
                formData.utmeSubjects.length === 4 && formData.program
                  ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              {loading ? "Checking eligibility..." : "Check Eligibility"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EligibilityForm;
